import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { COMPANY } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact — IDEAL ENTERPRISES" description="Talk to our team about your CGD or utility infrastructure package." />
      <PageHeader
        overline="GET IN TOUCH"
        title="Tell us about your project."
        subtitle="A short note is enough — our engineering team will respond within one business day."
        bgImage="https://images.pexels.com/photos/3818947/pexels-photo-3818947.jpeg"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">Direct Channels</p>
            <h2 className="font-heading font-bold text-3xl text-brand-blue mt-3">
              We'd love to hear from you.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              For quote requests, please use the dedicated <span className="font-semibold">Request a Quote</span> form for a faster turnaround.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 grid place-items-center bg-orange-100 text-brand-orange shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="overline">Office</p>
                  <p className="text-slate-700 mt-1">{COMPANY.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 grid place-items-center bg-orange-100 text-brand-orange shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="overline">Phone</p>
                  <a href={`tel:${COMPANY.phone}`} data-testid="contact-phone-link" className="text-slate-700 mt-1 inline-block hover:text-brand-orange">{COMPANY.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 grid place-items-center bg-orange-100 text-brand-orange shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="overline">Email</p>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="text-slate-700 mt-1 inline-block hover:text-brand-orange">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 grid place-items-center bg-orange-100 text-brand-orange shrink-0">
                  <Clock size={18} />
                </div>
                <div>
                  <p className="overline">Working Hours</p>
                  <p className="text-slate-700 mt-1">Mon–Sat · 09:00–18:30 IST</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-[#F8F9FA] border border-slate-200 p-8 md:p-10">
              <p className="overline">Send us a message</p>
              <h3 className="font-heading font-bold text-2xl text-brand-blue mt-3">Contact Form</h3>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name">Full Name *</Label>
                  <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-company">Company</Label>
                  <Input id="c-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" className="rounded-none mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="c-msg">Message *</Label>
                  <Textarea id="c-msg" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" className="rounded-none mt-1.5" />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-6 bg-brand-orange text-white px-8 py-3.5 font-semibold btn-lift hover:bg-orange-600 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <p className="overline">Find Us</p>
          <h2 className="font-heading font-bold text-3xl text-brand-blue mt-3">Head Office — Jalgaon</h2>
          <div className="mt-8 aspect-[16/7] overflow-hidden border border-slate-200">
            <iframe
              title="Office location"
              data-testid="contact-map"
              src={COMPANY.mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}
